import { useCallback, useEffect, useMemo, useRef } from 'react';
import { GroupBase } from 'react-select';
import AsyncSelect, { AsyncProps } from 'react-select/async';

import asyncSelectComponents, { AsyncMessage, LoadingMessage } from './async-override';

/** Пауза в наборе, после которой уходит запрос: AsyncSelect зовёт `loadOptions` на каждый ввод. */
export const SEARCH_DEBOUNCE_MS = 350;

export type UDAsyncSelectProps<Option, IsMulti extends boolean = false> = Omit<
  AsyncProps<Option, IsMulti, GroupBase<Option>>,
  'loadOptions'
> & {
  /** Поиск. Только промис-форма — колбэк react-select не поддерживается. */
  loadOptions: (inputValue: string) => Promise<readonly Option[]>;
  /** 0 отключает задержку. */
  debounceMs?: number;
  /**
   * Грузить выдачу сразу при открытии, не дожидаясь ввода: `loadOptions('')` уходит на
   * монтировании. По умолчанию выключено — иначе каждый поиск в приложении начал бы
   * ходить в сеть до того, как пользователь что-то ввёл.
   */
  preloadOptions?: boolean;
  /**
   * Что писать, пока ввод пуст, вместо «Нет совпадений»: список не пуст — его просто
   * ещё не о чем фильтровать.
   */
  emptyInputMessage?: string;
  /**
   * С какой длины ввода начинать искать. Пока введено меньше, запрос не уходит вовсе, а
   * вместо выдачи видна `minSearchMessage`. По умолчанию порога нет — поиски, которым он
   * не нужен, работают как раньше.
   */
  minSearchLength?: number;
  /** Что писать вместо выдачи, пока ввод короче `minSearchLength`. */
  minSearchMessage?: string;
};

/**
 * Откладывает поиск до паузы в наборе: пока пользователь печатает, таймер сбрасывается,
 * и запрос уходит один — с последним введённым значением.
 *
 * Промисы отменённых вызовов остаются неразрешёнными: react-select ждёт только последний,
 * а разрешать их пустым списком нельзя — в выдаче моргнёт «Нет совпадений».
 *
 * Таймер и сам `loadOptions` живут в ref'ах, поэтому возвращаемая функция стабильна и
 * переживает перерисовку даже с немемоизированным `loadOptions`.
 */
function useDebouncedLoadOptions<Option>(loadOptions: (inputValue: string) => Promise<readonly Option[]>, delay: number) {
  const loadOptionsRef = useRef(loadOptions);
  loadOptionsRef.current = loadOptions;

  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(timerRef.current), []);

  return useCallback(
    (inputValue: string) =>
      new Promise<readonly Option[]>((resolve) => {
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => resolve(loadOptionsRef.current(inputValue)), delay);
      }),
    [delay],
  );
}

/**
 * Асинхронный select приложения: поиск по мере ввода поверх `react-select/async`.
 *
 * Берёт на себя то, что одинаково у всех поисков — задержку перед запросом, кэш уже
 * найденного и оформление выдачи. Что и где искать, знает вызывающая сторона: она
 * передаёт `loadOptions` и доменный `SingleValue` в `components`.
 */
export function UDAsyncSelect<Option, IsMulti extends boolean = false>(props: UDAsyncSelectProps<Option, IsMulti>) {
  const {
    loadOptions,
    debounceMs = SEARCH_DEBOUNCE_MS,
    preloadOptions,
    emptyInputMessage,
    minSearchLength,
    minSearchMessage,
    components,
    ...restProps
  } = props;

  const debouncedLoadOptions = useDebouncedLoadOptions(loadOptions, debounceMs);

  const isBelowThreshold = useCallback(
    (inputValue: string) => Boolean(minSearchLength) && inputValue.length > 0 && inputValue.length < minSearchLength!,
    [minSearchLength],
  );

  // Короткий ввод отсекается до `loadOptions`, а не внутри него: иначе каждый набранный
  // символ уходил бы в сеть. Пустая выдача попадёт в `cacheOptions` под ключом самого
  // короткого ввода — на ключ полного запроса это не влияет, кэш у react-select по строке.
  const guardedLoadOptions = useCallback(
    (inputValue: string) =>
      isBelowThreshold(inputValue) ? Promise.resolve([] as readonly Option[]) : debouncedLoadOptions(inputValue),
    [isBelowThreshold, debouncedLoadOptions],
  );

  const selectComponents = useMemo(() => {
    const merged: Record<string, any> = { ...asyncSelectComponents, ...components };

    // «Нет совпадений» врёт дважды: при пустом вводе ничего и не искали, при коротком —
    // ещё не начинали. Подменяем только эти две ветки; на длинном вводе сообщение обычное.
    if (emptyInputMessage || minSearchMessage) {
      const NoOptionsMessage = merged.NoOptionsMessage;
      merged.NoOptionsMessage = (messageProps: any) => {
        const inputValue: string = messageProps.selectProps?.inputValue ?? '';

        if (!inputValue && emptyInputMessage) return <AsyncMessage>{emptyInputMessage}</AsyncMessage>;
        if (minSearchMessage && isBelowThreshold(inputValue)) return <AsyncMessage>{minSearchMessage}</AsyncMessage>;

        return <NoOptionsMessage {...messageProps} />;
      };
    }

    return merged;
  }, [components, emptyInputMessage, minSearchMessage, isBelowThreshold]);

  return (
    <AsyncSelect<Option, IsMulti, GroupBase<Option>>
      cacheOptions
      defaultOptions={preloadOptions}
      loadingMessage={LoadingMessage}
      {...restProps}
      loadOptions={guardedLoadOptions}
      components={selectComponents}
    />
  );
}
